import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from 'src/components/ui/dialog';
import { ScrollArea } from 'src/components/ui/scroll-area';
import { CircleNotchIcon } from '@phosphor-icons/react';
import { useListWorkflowVersions } from 'src/api/rpc/hooks/workflows/useWorkflowVersioning';
import { useGetWorkflow } from 'src/api/rpc/hooks/workflows/useWorkflowDefinitions';
import { T } from 'gt-react';
import { VersionLineageGraph } from './VersionLineageGraph';
import { WorkflowVersionHistoryRow } from './WorkflowVersionHistoryRow';

interface VersionHistoryPanelProps {
  open: boolean;
  onClose: () => void;
  lineageId: string;
  currentlyViewingId?: string;
  onPinChanged?: (newPinnedDefinitionId: string) => void;
}

export function VersionHistoryPanel({
  open,
  onClose,
  lineageId,
  currentlyViewingId,
  onPinChanged,
}: VersionHistoryPanelProps) {
  const { data, isLoading } = useListWorkflowVersions(
    open ? lineageId : undefined,
  );
  const { data: workflow } = useGetWorkflow(open ? lineageId : undefined);
  const versions = data?.versions ?? [];
  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent className="sm:max-w-3xl">
        <DialogHeader>
          <DialogTitle>
            <T context="workflow history dialog title">Version history</T>
          </DialogTitle>
          <DialogDescription>
            <T context="workflow history dialog description">
              Every version, joined to the version it was forked from. The
              highlighted path leads to the version you are viewing.
            </T>
          </DialogDescription>
        </DialogHeader>
        <ScrollArea className="max-h-[60vh]">
          {isLoading ? (
            <div className="flex items-center justify-center py-8 text-muted-foreground">
              <CircleNotchIcon className="h-4 w-4 animate-spin" />
            </div>
          ) : versions.length === 0 ? (
            <p className="py-8 text-center text-sm text-muted-foreground">
              <T context="empty workflow history">No versions yet.</T>
            </p>
          ) : (
            <VersionLineageGraph
              versions={versions}
              currentlyViewingId={currentlyViewingId}
              renderVersion={(version, isViewing) => (
                <WorkflowVersionHistoryRow
                  version={version}
                  versions={versions}
                  isViewing={isViewing}
                  isOnlyVersion={versions.length === 1}
                  isLastKnownGood={
                    version.id === workflow?.lastKnownGoodVersionId
                  }
                  stayInEditor
                  onPinChanged={onPinChanged}
                  onView={onClose}
                />
              )}
            />
          )}
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
}
